/**
 * WhoCanDoWhatPanel — auto-generated permission matrix.
 *
 * Fetches `/api/policies/{role}` for each of the 6 platform roles and
 * renders a tabular matrix of permissions × roles. The data is always
 * current — change the backend matrix and this UI reflects it on the
 * next page load.
 *
 * Phase A — "potential improvement" delivered alongside Legal & IP /
 * Master Rate Card gating. Surfaced inside the Admin → Help tab.
 */
import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Check, X, Shield } from "lucide-react";

const ROLES = [
  { key: "founder", label: "Founder", short: "T0" },
  { key: "sales_manager", label: "Sales mgr.", short: "T1" },
  { key: "sales_person", label: "Sales person", short: "T2" },
  { key: "trial_user", label: "Trial user", short: "T3" },
  { key: "campaign_user", label: "Campaign user", short: "T4" },
  { key: "lite_public", label: "Lite public", short: "T5" },
];

// Human-friendly labels for the permission keys
const PERMISSION_LABELS = {
  "admin.legal_ip.read": "Legal & IP read",
  "admin.rate_card.master.read": "Master rate card · read",
  "admin.rate_card.master.write": "Master rate card · write",
  "admin.rate_card.deal.create": "Deal rate card · create",
  "admin.rate_card.deal.write_own": "Deal rate card · write own",
  "admin.users.read": "Users · read",
  "admin.users.write": "Users · write",
  "admin.trials.review": "Trials · review",
  "admin.trials.approve": "Trials · approve",
  "admin.trials.promote_to_active_prospect": "Trials · promote to active prospect",
  "admin.campaigns.write": "Campaigns · write",
  "admin.audit_log.read.all": "Audit log · read all",
  "admin.pipeline.read.all": "Pipeline · read all",
  "admin.pipeline.read.own": "Pipeline · read own",
  "assessment.full.run.unlimited": "Full assessment · unlimited",
  "assessment.full.run.one_off": "Full assessment · one-off",
  "assessment.lite.run": "Lite assessment · run",
  "lite_assessment.public.run": "Lite assessment · public",
  "export.pricing.visible": "Exports · pricing visible",
  "impersonate": "Impersonate other users",
};

const ROW_GROUPS = [
  {
    heading: "Administration",
    keys: [
      "admin.legal_ip.read",
      "admin.users.read",
      "admin.users.write",
      "admin.audit_log.read.all",
      "admin.campaigns.write",
      "impersonate",
    ],
  },
  {
    heading: "Rate cards & pricing",
    keys: [
      "admin.rate_card.master.read",
      "admin.rate_card.master.write",
      "admin.rate_card.deal.create",
      "admin.rate_card.deal.write_own",
      "export.pricing.visible",
    ],
  },
  {
    heading: "Trials & pipeline",
    keys: [
      "admin.trials.review",
      "admin.trials.approve",
      "admin.trials.promote_to_active_prospect",
      "admin.pipeline.read.all",
      "admin.pipeline.read.own",
    ],
  },
  {
    heading: "Assessments",
    keys: [
      "assessment.full.run.unlimited",
      "assessment.full.run.one_off",
      "assessment.lite.run",
      "lite_assessment.public.run",
    ],
  },
];

export const WhoCanDoWhatPanel = () => {
  const [bundles, setBundles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [policyVersion, setPolicyVersion] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          ROLES.map((r) => api.get(`/policies/${r.key}`).then((res) => [r.key, res.data]))
        );
        if (cancelled) return;
        const map = Object.fromEntries(results);
        setBundles(map);
        setPolicyVersion(map.founder?.policy_bundle_version || null);
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Failed to load policy bundles");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="p-6 text-zinc-500 text-sm" data-testid="who-can-do-what-loading">
        Loading permission matrix…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 text-red-600 text-sm" data-testid="who-can-do-what-error">
        {error}
      </div>
    );
  }

  const Cell = ({ roleKey, permKey }) => {
    const perms = bundles[roleKey]?.permissions || [];
    const granted = perms.includes(permKey);
    return (
      <td
        className="text-center px-2 py-2 border-b border-zinc-100"
        data-testid={`perm-cell-${roleKey}-${permKey}`}
      >
        {granted ? (
          <Check className="w-4 h-4 text-[#7737F6] inline" aria-label="granted" />
        ) : (
          <X className="w-4 h-4 text-zinc-300 inline" aria-label="not granted" />
        )}
      </td>
    );
  };

  return (
    <div className="space-y-6" data-testid="who-can-do-what-panel">
      <div className="flex items-start gap-3 p-4 bg-[#7737F6]/5 border border-[#7737F6]/20 rounded">
        <Shield className="w-5 h-5 text-[#7737F6] flex-shrink-0 mt-0.5" />
        <div className="text-[13px] text-zinc-700 leading-relaxed">
          <p className="font-medium text-black mb-1">Permission matrix — auto-generated</p>
          <p>
            This page is rendered live from{" "}
            <code className="text-[12px] bg-zinc-100 px-1 py-0.5 rounded">/api/policies/{`{role}`}</code>.
            Any change to the backend matrix is reflected here on the next refresh — no manual maintenance.
          </p>
          {policyVersion && (
            <p className="mt-2 text-zinc-500">
              Policy bundle version: <span className="font-mono">{policyVersion}</span>
            </p>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-[13px] border-collapse" data-testid="permission-matrix-table">
          <thead>
            <tr className="bg-zinc-50">
              <th className="text-left px-3 py-2 font-medium text-zinc-600 border-b border-zinc-200 min-w-[280px]">
                Capability
              </th>
              {ROLES.map((r) => (
                <th
                  key={r.key}
                  className="text-center px-2 py-2 font-medium text-zinc-600 border-b border-zinc-200 min-w-[110px]"
                  data-testid={`matrix-col-${r.key}`}
                >
                  <div className="text-black">{r.label}</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">{r.short}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROW_GROUPS.map((group) => (
              <React.Fragment key={group.heading}>
                <tr>
                  <td
                    colSpan={1 + ROLES.length}
                    className="px-3 py-3 text-[11px] tracking-[0.14em] uppercase text-zinc-500 border-b border-zinc-200 bg-white"
                  >
                    {group.heading}
                  </td>
                </tr>
                {group.keys.map((permKey) => (
                  <tr key={permKey} className="hover:bg-zinc-50/60">
                    <td className="px-3 py-2 text-black border-b border-zinc-100" data-testid={`perm-row-${permKey}`}>
                      {PERMISSION_LABELS[permKey] || permKey}
                    </td>
                    {ROLES.map((r) => (
                      <Cell key={r.key} roleKey={r.key} permKey={permKey} />
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pt-4 border-t border-zinc-200 text-[12px] text-zinc-500 leading-relaxed">
        Rank order:{" "}
        {ROLES.map((r) => (
          <span key={r.key} className="font-mono">
            {r.short}
            {r.key !== "lite_public" && " → "}
          </span>
        ))}
        . Sales-manager (T1) and sales-person (T2) can both approve trial requests, but only
        T0/T1 can promote a trial to an active prospect (this binds a deal rate-card slot).
      </div>
    </div>
  );
};

export default WhoCanDoWhatPanel;
